/**
 * "Read again" (#395): ask DaVinci to re-run the readers that failed on an upload.
 *
 * A failed read is not the end of an upload. One refusal before anything is sent (no
 * budget period yet, no key, no consent) or one provider outage used to strand a page for
 * good; now the learner can ask again. The server re-runs ONLY the reader slots whose latest
 * attempt failed, keeps every reading that succeeded, and keeps the failed attempt on record.
 *
 * The retry is a background job, so after the click this screen asks again for a short
 * while - long enough for a read to finish, short enough that a retry that fails again does
 * not leave the screen polling for good. No spinner with a percentage, no count: it says,
 * in words, that it is reading again.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useHomeworkLabels, type HomeworkLabels } from '../../i18n/homework-labels.js';
import { usageMessages, useModelLocked } from '../usage/usage-meter.js';
import { useMessages } from '../../i18n/locale.js';

/** How long after a "Read again" the screen keeps asking the server what happened. */
export const READ_AGAIN_WINDOW_MS = 30_000;

export interface ReadAgainState {
  /** True from the click until the window closes: the screen should keep asking the server. */
  readonly readingAgain: boolean;
  readonly pending: boolean;
  readonly error: unknown;
  readonly request: () => void;
}

export function useReadAgain(uploadId: string): ReadAgainState {
  const api = useApi();
  const queryClient = useQueryClient();
  const [readingAgain, setReadingAgain] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
    },
    [],
  );

  const mutation = useMutation({
    mutationFn: () => api.readHomeworkAgain(uploadId, crypto.randomUUID()),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.homeworkUpload(uploadId), updated);
      // Every page's transcription may change when a retry succeeds.
      void queryClient.invalidateQueries({ queryKey: ['homework', 'transcription'] });
      setReadingAgain(true);
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = setTimeout(() => setReadingAgain(false), READ_AGAIN_WINDOW_MS);
    },
  });

  const request = useCallback(() => {
    mutation.reset();
    mutation.mutate();
  }, [mutation]);

  return { readingAgain, pending: mutation.isPending, error: mutation.error, request };
}

/** What the server's refusal of a read-again means to the learner; undefined for a code this screen has no sentence for. */
function readAgainFailureSentence(error: unknown, t: HomeworkLabels): string | undefined {
  if (!(error instanceof ApiRequestError)) return undefined;
  switch (error.code) {
    case 'consent_required':
      return t.readAgainNoConsent;
    case 'not_found':
      return t.uploadNotFound;
    default:
      return undefined;
  }
}

export function ReadAgainButton({
  state,
  label,
}: {
  readonly state: ReadAgainState;
  readonly label?: string;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  const locked = useModelLocked();
  const usage = useMessages(usageMessages);
  const sentence = state.error === null ? undefined : readAgainFailureSentence(state.error, t);
  return (
    <div className="read-again">
      {state.error !== null && state.error !== undefined ? (
        sentence !== undefined ? (
          <div className="notice notice-error" role="alert">
            <p>{sentence}</p>
          </div>
        ) : (
          <FailureNotice error={state.error} />
        )
      ) : null}
      {state.readingAgain ? (
        <p role="status">{t.readingAgainStatus}</p>
      ) : null}
      <button type="button" disabled={state.pending || state.readingAgain || locked} onClick={state.request}>
        {label ?? t.readAgain}
      </button>
      {locked ? <p className="field-hint">{usage.modelLockReason}</p> : null}
    </div>
  );
}
